import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { ViewerOpenContextKind, type ContentViewerSession, type ViewerOpenContext } from '@/content-viewer/store/schemas'
import { RenderFormat } from '@/content-viewer/store/schemas'
import type { AppDispatch } from '@/store/store'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { buildUserFileRendererConfig } from '@/user_files/files/utils/build-user-file-renderer-config'
import { buildUserFileViewerSession } from '@/user_files/files/utils/build-user-file-viewer-session'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import { type TimelineSourceRef } from './schemas'

export type OpenTimelineSourceViewerOptions = {
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
  referenceId?: string
}

export type OpenTimelineSourceViewerResult =
  | {
      opened: true
      session: ContentViewerSession
    }
  | {
      opened: false
      error: string
    }

type OpenTimelineSourceViewerArgs = {
  source: TimelineSourceRef
  options?: OpenTimelineSourceViewerOptions
  loadedUserFilesById: Record<string, UserFileRead>
  dispatch: AppDispatch
}

type TimelineViewerContext = Extract<ViewerOpenContext, { kind: ViewerOpenContextKind.TIMELINE_REFERENCE | ViewerOpenContextKind.TIMELINE_SOURCE }>

function buildTimelineOpenContext(source: TimelineSourceRef, options: OpenTimelineSourceViewerOptions): TimelineViewerContext {
  if (options.referenceId) {
    return {
      kind: ViewerOpenContextKind.TIMELINE_REFERENCE,
      referenceId: options.referenceId,
      sourceId: source.source_id,
    }
  }

  return {
    kind: ViewerOpenContextKind.TIMELINE_SOURCE,
    sourceId: source.source_id,
  }
}

/**
 * Resolves a timeline source into a normalized content-viewer session (ADR 002).
 *
 * Timeline sources only come from the user-files pipeline today, so unlike the
 * med-chron sibling there is no legacy branch: resolve the user file (lazily
 * fetching it when not already loaded), derive the render config, and carry
 * highlights/startingPage as presentation data on the session.
 */
export async function openTimelineSourceViewer(args: OpenTimelineSourceViewerArgs): Promise<OpenTimelineSourceViewerResult> {
  const { source, options = {}, loadedUserFilesById, dispatch } = args

  let userFile = loadedUserFilesById[source.source_id]
  if (!userFile) {
    const records = await fetchAndStoreUserFiles([source.source_id], dispatch)
    userFile = records[source.source_id]
  }

  const baseRenderer = userFile ? buildUserFileRendererConfig(userFile) : null
  if (!userFile || !baseRenderer) {
    return { opened: false, error: 'Could not load this source file.' }
  }

  // Timeline sources are PDFs today. If they ever aren't, highlights/startingPage
  // would need a non-PDF equivalent (e.g. sentence-based highlights for markdown).
  const renderer =
    baseRenderer.format === RenderFormat.PDF
      ? {
          ...baseRenderer,
          ...(options.highlights ? { highlights: options.highlights } : {}),
          ...(options.startingPage !== undefined ? { startingPage: options.startingPage } : {}),
        }
      : baseRenderer

  const session = buildUserFileViewerSession({
    userFile,
    renderer,
    context: buildTimelineOpenContext(source, options),
  })

  return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
}
